import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import uuidv1 from "uuid/v1";

import { saveField } from "../api/ProjectApi";
import { DialogTypes } from "../constants/action-types";
import DialogLayout from "./DialogLayout";
import Select from "react-select";
import Input from "../components/Input";
import PrimaryButton from "../components/PrimaryButton";

const FIELD_TYPE_OPTIONS = [
  { label: "Text", value: "string" },
  { label: "Number", value: "double" },
  { label: "Yes/No", value: "boolean" },
];

const INITIAL_FIELD_STATE = {
  label: "",
  type: "string",
  projectId: "",
  tenantId: "",
  isCustomField: true,
  selector: "",
  formType: "SALE",
};

const CustomFieldDialog = (props) => {
  const { t } = useTranslation();
  const { onClose, onSubmit, onDelete } = props;

  const [customField, setCustomField] = useState(INITIAL_FIELD_STATE);
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [deleting, setDeleting] = useState(false);

  // Redux selectors
  const show = useSelector((state: any) => state.grid.getIn(["showGridDialog", "show"]));
  const project = useSelector((state: any) => state.projectReducer.get("project"));
  const dialogType = useSelector((state: any) => state.grid.getIn(["showGridDialog", "type"]));
  const dialogId = useSelector((state: any) => state.grid.getIn(["showGridDialog", "id"]));
  const nameColumn = useSelector((state: any) => state.grid.getIn(["editColumn", "name"]));
  const selector = useSelector((state: any) => state.grid.getIn(["editColumn", "selector"]));
  const keyColumn = useSelector((state: any) => state.grid.getIn(["editColumn", "key"]));
  const formType = useSelector((state: any) => state.grid.getIn(["editColumn", "formType"]));
  const fields = project?.get("fields") || fromJS([]);

  // Computed values
  const isUpdatingField = dialogType === DialogTypes.updateCustomFieldDialog;
  const visible =
    show &&
    (dialogType === DialogTypes.createCustomFieldDialog ||
      dialogType === DialogTypes.updateCustomFieldDialog);
  const selectedType = useMemo(
    () => FIELD_TYPE_OPTIONS.find((item) => item.value === customField.type),
    [customField.type]
  );

  const handleFieldChange = (fieldId: string, fieldValue: any) => {
    setCustomField({
      ...customField,
      formType,
      [fieldId]: fieldValue,
    } as any);
  };

  const prepareFieldData = () => {
    const fieldData: any = {
      type: customField.type,
      label: customField.label,
      formType: customField.formType,
      projectId: customField.projectId,
      tenantId: customField.tenantId,
      isCustomField: customField.isCustomField,
      selector: customField.selector,
    };

    if ((customField as any).id) {
      fieldData.id = (customField as any).id;
    }

    if ((customField as any).key) {
      fieldData.key = (customField as any).key;
    } else if (customField.selector) {
      fieldData.key = customField.selector;
    }

    // Remove empty fields
    Object.keys(fieldData).forEach((key) => {
      if (fieldData[key] === "" || fieldData[key] === undefined) {
        delete fieldData[key];
      }
    });

    return fieldData;
  };

  const handleSave = async () => {
    if (!customField.label || !customField.type) {
      setErrorMessage(t("Type, Label are required."));
      return;
    }

    const fieldData = prepareFieldData();
    console.log("Saving field with data:", fieldData);

    try {
      await saveField(project.get("id"), fieldData);
      setErrorMessage(undefined);
      onSubmit();
    } catch ({ response }) {
      // Ignore 500 errors as data is still processed successfully on the backend
      if (response?.status === 500) {
        setErrorMessage(undefined);
        onSubmit();
        return;
      }
      const errorMsg = response?.data?.message || response?.data?.error || t("An error occurred");
      setErrorMessage(errorMsg);
      console.error("Error saving field:", {
        error: response?.data,
        fieldData,
        formType: customField.formType,
      });
    }
  };

  const loadFieldData = () => {
    if (!isUpdatingField || !dialogId) {
      return null;
    }
    return fields.find(
      (field) => field.get("id") === dialogId && field.get("formType") === formType
    );
  };

  const getFieldId = (fieldData: any) => {
    if (isUpdatingField) {
      return selector || dialogId || fieldData?.get("id") || "";
    }
    return selector || uuidv1();
  };

  const buildFieldState = (fieldId: string, fieldData: any) => {
    return {
      ...customField,
      id: fieldId,
      selector: fieldId,
      key: keyColumn || fieldData?.get("key") || fieldId,
      label: nameColumn || fieldData?.get("label") || "",
      type: fieldData?.get("type") || customField.type || "string",
      formType: formType || fieldData?.get("formType") || customField.formType,
      projectId: project?.get("id") || "",
      tenantId: project?.get("tenantId") || "",
      isCustomField: fieldData?.get("isCustomField") ?? true,
    };
  };

  const getFieldIdForDelete = () => {
    return (customField as any).id || customField.selector || dialogId || selector;
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (deleting) return;

    const fieldId = getFieldIdForDelete();
    if (!fieldId) {
      console.error("Cannot delete: field ID is missing", {
        customField,
        dialogId,
        selector,
      });
      setErrorMessage(t("Cannot delete: field ID is missing"));
      return;
    }

    try {
      setDeleting(true);
      await onDelete(fieldId);
    } catch (error: any) {
      // Ignore 500 errors as data is still processed successfully on the backend
      if (error?.response?.status !== 500) {
        console.error("Error deleting field:", error);
        setErrorMessage(t("Failed to delete field"));
      }
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (!show) {
      setCustomField(INITIAL_FIELD_STATE);
      setDeleting(false);
      setErrorMessage(undefined);
      return;
    }

    const fieldData = loadFieldData();
    const fieldId = getFieldId(fieldData);
    const newField = buildFieldState(fieldId, fieldData);
    setCustomField(newField);
  }, [show, formType, selector, keyColumn, nameColumn, project, isUpdatingField, dialogId, fields]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <DialogLayout show={visible}>
      <div className={"font-medium divide-gray-900/5 bg-gray-50 h-12 items-center flex"}>
        <div className={"p-2"}>{isUpdatingField ? "Edit field" : "New field"}</div>
      </div>
      <div className={"p-4 w-full"}>
        {!isUpdatingField && (
          <p className={"py-2 font-medium"}>
            This field will be available to all items in this workspace.
          </p>
        )}
        {errorMessage && <div className={"text-red-600 pb-2 font-medium"}>{errorMessage}</div>}
        <div className={"space-y-1"}>
          {!isUpdatingField && (
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">Type</label>
              <Select
                options={FIELD_TYPE_OPTIONS}
                onChange={({ value }) => handleFieldChange("type", value)}
                value={selectedType}
              />
            </div>
          )}
          <Input
            label="Label"
            placeholder={"e.g Duration"}
            value={customField.label}
            onChange={(value) => handleFieldChange("label", value)}
          />
        </div>
        <div className={"flex justify-end space-x-2 pt-2 align-middle"}>
          <PrimaryButton label={isUpdatingField ? "Update" : "Create"} onClick={handleSave} />
          <div
            className={"hover:text-blue-600 cursor-pointer p-2 w-20 justify-center flex"}
            onClick={onClose}
          >
            Close
          </div>
          {isUpdatingField && (
            <div
              onClick={handleDelete}
              className={`p-2 w-20 justify-center flex ${
                deleting
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-red-600 cursor-pointer hover:text-red-800"
              }`}
            >
              {deleting ? t("Deleting...") : t("Delete")}
            </div>
          )}
        </div>
      </div>
    </DialogLayout>
  );
};

export default CustomFieldDialog;
